import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { 
  AlertCircle, 
  Loader2, 
  ChevronLeft,
  Package,
  Sparkles,
  Mail,
  Phone,
  Check,
  ArrowRight
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Style options with visual representations
const styleOptions = [
  { 
    value: "bikini", 
    label: "Bikini", 
    description: "Classic & comfy",
    emoji: "👙",
    color: "from-pink-100 to-rose-100"
  },
  { 
    value: "brief", 
    label: "Brief", 
    description: "Full coverage",
    emoji: "🩲",
    color: "from-purple-100 to-indigo-100"
  },
  { 
    value: "thong", 
    label: "Thong", 
    description: "Minimal & cheeky",
    emoji: "✨",
    color: "from-coral-100 to-orange-100"
  },
  { 
    value: "boxer", 
    label: "Boyshort", 
    description: "Extra coverage",
    emoji: "🎀",
    color: "from-teal-100 to-cyan-100"
  },
];

// Color options
const colorOptions = [
  { value: "dark", label: "Dark colors", emoji: "🖤", description: "Black, navy, deep tones" },
  { value: "neutral", label: "Neutral", emoji: "🤍", description: "Nude, beige, soft tones" },
  { value: "no_preference", label: "Surprise me!", emoji: "🎁", description: "We'll pick something cute" },
];

// Size options
const sizeOptions = ["XS", "S", "M", "L", "XL", "2XL"];

export default function Request() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [intakePaused, setIntakePaused] = useState(false);
  const [checkingSettings, setCheckingSettings] = useState(true);
  const [error, setError] = useState("");
  
  // Current step in the flow
  const [step, setStep] = useState(1);
  const [direction, setDirection] = useState("forward"); // for animation direction
  
  // Form data
  const [formData, setFormData] = useState({
    style: "",
    size: "",
    color: "",
    street: "",
    apt: "",
    city: "",
    state: "",
    zip: "",
    name: "",
    wantMore: null, // null = not answered, true/false
    contactMethod: "", // email or phone
    contactValue: "",
  });

  useEffect(() => {
    fetch(`${API}/settings`)
      .then((res) => res.json())
      .then((data) => {
        setIntakePaused(data.intake_paused);
        setCheckingSettings(false);
      })
      .catch(() => setCheckingSettings(false));
  }, []);

  const goNext = () => {
    setDirection("forward");
    setError("");
    setStep((s) => s + 1);
  };

  const goBack = () => {
    setDirection("back");
    setError("");
    setStep((s) => Math.max(1, s - 1));
  };

  const selectStyle = (style) => {
    setFormData({ ...formData, style });
    setTimeout(goNext, 300);
  };

  const selectSize = (size) => {
    setFormData({ ...formData, size });
    setTimeout(goNext, 300);
  };

  const selectColor = (color) => {
    setFormData({ ...formData, color });
    setTimeout(goNext, 300);
  };

  const selectWantMore = (want) => {
    setFormData({ ...formData, wantMore: want });
    if (want) {
      setTimeout(goNext, 300); // Go to contact method step
    } else {
      setStep(8); // Skip to review
    }
  };

  const selectContactMethod = (method) => {
    setFormData({ ...formData, contactMethod: method });
    // Don't auto-advance - user needs to enter contact value first
  };

  const validateAddress = () => {
    if (!formData.street || formData.street.length < 5) {
      setError("We need a street address to ship to.");
      return false;
    }
    if (!formData.city) {
      setError("What city?");
      return false;
    }
    if (!formData.state) {
      setError("What state?");
      return false;
    }
    if (!formData.zip || formData.zip.length < 5) {
      setError("We need a zip code.");
      return false;
    }
    return true;
  };

  const validateName = () => {
    if (!formData.name || formData.name.length < 1) {
      setError("We need a name for the package - any name works!");
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError("");

    try {
      // Build shipping address
      const addressParts = [formData.name, formData.street];
      if (formData.apt) addressParts.push(formData.apt);
      addressParts.push(`${formData.city}, ${formData.state} ${formData.zip}`);
      const shipping_address = addressParts.join("\n");

      const payload = {
        shipping_address,
        size: formData.size === "2XL" ? "XL" : formData.size, // Map 2XL to XL for backend
        cut_preference: formData.style || null,
        color_preference: formData.color || null,
        is_eligible: true,
        work_categories: null,
        needs_text: null,
        opted_in: formData.wantMore === true,
        contact_method: formData.contactMethod === "email" ? "email" : formData.contactMethod === "phone" ? "text" : null,
        contact_value: formData.contactValue || null,
      };

      const response = await fetch(`${API}/requests`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || "Something went wrong");
      }

      const data = await response.json();
      toast.success("They're on their way! 🎉");
      navigate("/thank-you", { state: { requestCode: data.request_code, optedIn: formData.wantMore } });
    } catch (err) {
      setError(err.message);
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (checkingSettings) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[color:var(--pws-coral)]" />
      </div>
    );
  }

  if (intakePaused) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center p-4">
        <div className="max-w-md text-center">
          <Package className="h-16 w-16 mx-auto text-[color:var(--pws-stone)] mb-4" />
          <h1 className="font-['Space_Grotesk'] text-2xl font-semibold text-[color:var(--pws-charcoal)]">
            Taking a breather
          </h1>
          <p className="mt-4 text-[color:var(--pws-stone)]">
            We're temporarily paused while we restock. Check back soon!
          </p>
          <Button asChild className="mt-6 btn-warm">
            <Link to="/">Back to home</Link>
          </Button>
        </div>
      </div>
    );
  }

  // Animation classes
  const slideClass = direction === "forward" 
    ? "animate-in slide-in-from-right-8 fade-in duration-300" 
    : "animate-in slide-in-from-left-8 fade-in duration-300";

  return (
    <div className="min-h-[calc(100vh-80px)] bg-gradient-to-b from-[color:var(--pws-off)] to-[color:var(--pws-cream)] flex flex-col">
      {/* Progress dots */}
      <div className="pt-6 pb-4 px-4">
        <div className="flex justify-center gap-2">
          {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
            <div
              key={i}
              className={`h-2 rounded-full transition-all duration-300 ${
                i === step
                  ? "w-8 bg-[color:var(--pws-coral)]"
                  : i < step
                  ? "w-2 bg-[color:var(--pws-coral)]/50"
                  : "w-2 bg-[color:var(--pws-line)]"
              }`}
            />
          ))}
        </div>
      </div>

      {/* Main content area */}
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-lg">
          {error && (
            <Alert variant="destructive" className="mb-6">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Step 1: Style */}
          {step === 1 && (
            <div className={slideClass} key="step1">
              <div className="text-center mb-8">
                <h1 className="font-['Space_Grotesk'] text-3xl md:text-4xl font-bold text-[color:var(--pws-charcoal)]">
                  What's your style?
                </h1>
                <p className="mt-2 text-[color:var(--pws-stone)]">
                  Pick what feels right
                </p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {styleOptions.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => selectStyle(option.value)}
                    className={`group relative p-6 rounded-2xl border-2 transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] ${
                      formData.style === option.value
                        ? "border-[color:var(--pws-coral)] bg-[color:var(--pws-coral)]/5 shadow-lg"
                        : "border-[color:var(--pws-line)] bg-white hover:border-[color:var(--pws-coral)]/50 hover:shadow-md"
                    }`}
                    data-testid={`style-${option.value}`}
                  >
                    <div className="text-4xl mb-3">{option.emoji}</div>
                    <div className="font-semibold text-[color:var(--pws-charcoal)]">{option.label}</div>
                    <div className="text-sm text-[color:var(--pws-stone)] mt-1">{option.description}</div>
                    {formData.style === option.value && (
                      <div className="absolute top-3 right-3 w-6 h-6 bg-[color:var(--pws-coral)] rounded-full flex items-center justify-center">
                        <Check className="h-4 w-4 text-white" />
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 2: Size */}
          {step === 2 && (
            <div className={slideClass} key="step2">
              <div className="text-center mb-8">
                <h1 className="font-['Space_Grotesk'] text-3xl md:text-4xl font-bold text-[color:var(--pws-charcoal)]">
                  What size?
                </h1>
                <p className="mt-2 text-[color:var(--pws-stone)]">
                  Standard underwear sizing
                </p>
              </div>
              <div className="grid grid-cols-3 gap-3">
                {sizeOptions.map((size) => (
                  <button
                    key={size}
                    onClick={() => selectSize(size)}
                    className={`py-5 px-4 rounded-2xl border-2 font-bold text-xl transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] ${
                      formData.size === size
                        ? "border-[color:var(--pws-coral)] bg-[color:var(--pws-coral)] text-white shadow-lg"
                        : "border-[color:var(--pws-line)] bg-white text-[color:var(--pws-charcoal)] hover:border-[color:var(--pws-coral)]/50 hover:shadow-md"
                    }`}
                    data-testid={`size-${size}`}
                  >
                    {size}
                  </button>
                ))}
              </div>
              <button
                onClick={goBack}
                className="mt-6 flex items-center justify-center w-full text-[color:var(--pws-stone)] hover:text-[color:var(--pws-charcoal)] transition-colors"
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Back
              </button>
            </div>
          )}

          {/* Step 3: Color */}
          {step === 3 && (
            <div className={slideClass} key="step3">
              <div className="text-center mb-8">
                <h1 className="font-['Space_Grotesk'] text-3xl md:text-4xl font-bold text-[color:var(--pws-charcoal)]">
                  Color preference?
                </h1>
                <p className="mt-2 text-[color:var(--pws-stone)]">
                  We'll do our best to match
                </p>
              </div>
              <div className="space-y-3">
                {colorOptions.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => selectColor(option.value)}
                    className={`w-full p-5 rounded-2xl border-2 text-left transition-all duration-200 hover:scale-[1.01] active:scale-[0.99] flex items-center gap-4 ${
                      formData.color === option.value
                        ? "border-[color:var(--pws-coral)] bg-[color:var(--pws-coral)]/5 shadow-lg"
                        : "border-[color:var(--pws-line)] bg-white hover:border-[color:var(--pws-coral)]/50 hover:shadow-md"
                    }`}
                    data-testid={`color-${option.value}`}
                  >
                    <span className="text-3xl">{option.emoji}</span>
                    <div>
                      <div className="font-semibold text-[color:var(--pws-charcoal)]">{option.label}</div>
                      <div className="text-sm text-[color:var(--pws-stone)]">{option.description}</div>
                    </div>
                    {formData.color === option.value && (
                      <div className="ml-auto w-6 h-6 bg-[color:var(--pws-coral)] rounded-full flex items-center justify-center">
                        <Check className="h-4 w-4 text-white" />
                      </div>
                    )}
                  </button>
                ))}
              </div>
              <button
                onClick={goBack}
                className="mt-6 flex items-center justify-center w-full text-[color:var(--pws-stone)] hover:text-[color:var(--pws-charcoal)] transition-colors"
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Back
              </button>
            </div>
          )}

          {/* Step 4: Address */}
          {step === 4 && (
            <div className={slideClass} key="step4">
              <div className="text-center mb-8">
                <h1 className="font-['Space_Grotesk'] text-3xl md:text-4xl font-bold text-[color:var(--pws-charcoal)]">
                  Where should we send them?
                </h1>
                <p className="mt-2 text-[color:var(--pws-stone)]">
                  We ship discreetly, no branding on the package
                </p>
              </div>
              <div className="space-y-4 bg-white p-6 rounded-2xl border border-[color:var(--pws-line)]">
                <div>
                  <Label htmlFor="street" className="text-[color:var(--pws-charcoal)] font-medium">Street address</Label>
                  <Input
                    id="street"
                    placeholder="123 Main St"
                    value={formData.street}
                    onChange={(e) => setFormData({ ...formData, street: e.target.value })}
                    className="mt-1.5 h-12 rounded-xl border-[color:var(--pws-line)]"
                    data-testid="address-street"
                  />
                </div>
                <div>
                  <Label htmlFor="apt" className="text-[color:var(--pws-charcoal)] font-medium">Apt, suite, etc. <span className="text-[color:var(--pws-stone)] font-normal">(optional)</span></Label>
                  <Input
                    id="apt"
                    placeholder="Apt 4B"
                    value={formData.apt}
                    onChange={(e) => setFormData({ ...formData, apt: e.target.value })}
                    className="mt-1.5 h-12 rounded-xl border-[color:var(--pws-line)]"
                    data-testid="address-apt"
                  />
                </div>
                <div className="grid grid-cols-5 gap-3">
                  <div className="col-span-2">
                    <Label htmlFor="city" className="text-[color:var(--pws-charcoal)] font-medium">City</Label>
                    <Input
                      id="city"
                      placeholder="New York"
                      value={formData.city}
                      onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                      className="mt-1.5 h-12 rounded-xl border-[color:var(--pws-line)]"
                      data-testid="address-city"
                    />
                  </div>
                  <div className="col-span-1">
                    <Label htmlFor="state" className="text-[color:var(--pws-charcoal)] font-medium">State</Label>
                    <Input
                      id="state"
                      placeholder="NY"
                      maxLength={2}
                      value={formData.state}
                      onChange={(e) => setFormData({ ...formData, state: e.target.value.toUpperCase() })}
                      className="mt-1.5 h-12 rounded-xl border-[color:var(--pws-line)]"
                      data-testid="address-state"
                    />
                  </div>
                  <div className="col-span-2">
                    <Label htmlFor="zip" className="text-[color:var(--pws-charcoal)] font-medium">Zip</Label>
                    <Input
                      id="zip"
                      placeholder="10001"
                      maxLength={10}
                      value={formData.zip}
                      onChange={(e) => setFormData({ ...formData, zip: e.target.value })}
                      className="mt-1.5 h-12 rounded-xl border-[color:var(--pws-line)]"
                      data-testid="address-zip"
                    />
                  </div>
                </div>
              </div>
              <div className="mt-6 flex gap-3">
                <Button
                  variant="outline"
                  onClick={goBack}
                  className="flex-1 h-12 rounded-xl border-[color:var(--pws-line)]"
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Back
                </Button>
                <Button
                  onClick={() => validateAddress() && goNext()}
                  className="flex-1 btn-warm h-12 rounded-xl"
                  data-testid="address-next"
                >
                  Next
                  <ArrowRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </div>
          )}

          {/* Step 5: Name */}
          {step === 5 && (
            <div className={slideClass} key="step5">
              <div className="text-center mb-8">
                <h1 className="font-['Space_Grotesk'] text-3xl md:text-4xl font-bold text-[color:var(--pws-charcoal)]">
                  What name for the package?
                </h1>
                <p className="mt-2 text-[color:var(--pws-stone)]">
                  First name only is cool. Made-up name? Also cool.
                </p>
              </div>
              <div className="bg-white p-6 rounded-2xl border border-[color:var(--pws-line)]">
                <Input
                  placeholder="Whatever works for you"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="h-14 rounded-xl border-[color:var(--pws-line)] text-lg text-center"
                  data-testid="name-input"
                  autoFocus
                />
                <p className="mt-3 text-sm text-[color:var(--pws-stone)] text-center">
                  We don't need your government name 💜
                </p>
              </div>
              <div className="mt-6 flex gap-3">
                <Button
                  variant="outline"
                  onClick={goBack}
                  className="flex-1 h-12 rounded-xl border-[color:var(--pws-line)]"
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Back
                </Button>
                <Button
                  onClick={() => validateName() && goNext()}
                  className="flex-1 btn-warm h-12 rounded-xl"
                  data-testid="name-next"
                >
                  Next
                  <ArrowRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </div>
          )}

          {/* Step 6: Want more free stuff? */}
          {step === 6 && (
            <div className={slideClass} key="step6">
              <div className="text-center mb-8">
                <Sparkles className="h-12 w-12 mx-auto text-[color:var(--pws-coral)] mb-4" />
                <h1 className="font-['Space_Grotesk'] text-3xl md:text-4xl font-bold text-[color:var(--pws-charcoal)]">
                  Want more free stuff?
                </h1>
                <p className="mt-2 text-[color:var(--pws-stone)]">
                  We sometimes have more goodies to share
                </p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <button
                  onClick={() => selectWantMore(true)}
                  className={`p-8 rounded-2xl border-2 transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] ${
                    formData.wantMore === true
                      ? "border-[color:var(--pws-coral)] bg-[color:var(--pws-coral)]/5 shadow-lg"
                      : "border-[color:var(--pws-line)] bg-white hover:border-[color:var(--pws-coral)]/50"
                  }`}
                  data-testid="want-more-yes"
                >
                  <div className="text-4xl mb-2">🙋‍♀️</div>
                  <div className="font-bold text-xl text-[color:var(--pws-charcoal)]">Yes please!</div>
                </button>
                <button
                  onClick={() => selectWantMore(false)}
                  className={`p-8 rounded-2xl border-2 transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] ${
                    formData.wantMore === false
                      ? "border-[color:var(--pws-coral)] bg-[color:var(--pws-coral)]/5 shadow-lg"
                      : "border-[color:var(--pws-line)] bg-white hover:border-[color:var(--pws-coral)]/50"
                  }`}
                  data-testid="want-more-no"
                >
                  <div className="text-4xl mb-2">😊</div>
                  <div className="font-bold text-xl text-[color:var(--pws-charcoal)]">I'm good</div>
                </button>
              </div>
              <p className="mt-4 text-center text-sm text-[color:var(--pws-stone)]">
                No pressure either way
              </p>
              <button
                onClick={goBack}
                className="mt-6 flex items-center justify-center w-full text-[color:var(--pws-stone)] hover:text-[color:var(--pws-charcoal)] transition-colors"
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Back
              </button>
            </div>
          )}

          {/* Step 7: Contact method */}
          {step === 7 && (
            <div className={slideClass} key="step7">
              <div className="text-center mb-8">
                <h1 className="font-['Space_Grotesk'] text-3xl md:text-4xl font-bold text-[color:var(--pws-charcoal)]">
                  How should we reach you?
                </h1>
                <p className="mt-2 text-[color:var(--pws-stone)]">
                  For the free stuff updates only
                </p>
              </div>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <button
                  onClick={() => selectContactMethod("email")}
                  className={`p-6 rounded-2xl border-2 transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] ${
                    formData.contactMethod === "email"
                      ? "border-[color:var(--pws-coral)] bg-[color:var(--pws-coral)]/5 shadow-lg"
                      : "border-[color:var(--pws-line)] bg-white hover:border-[color:var(--pws-coral)]/50"
                  }`}
                  data-testid="contact-email"
                >
                  <Mail className="h-8 w-8 mx-auto mb-2 text-[color:var(--pws-coral)]" />
                  <div className="font-semibold text-[color:var(--pws-charcoal)]">Email</div>
                </button>
                <button
                  onClick={() => selectContactMethod("phone")}
                  className={`p-6 rounded-2xl border-2 transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] ${
                    formData.contactMethod === "phone"
                      ? "border-[color:var(--pws-coral)] bg-[color:var(--pws-coral)]/5 shadow-lg"
                      : "border-[color:var(--pws-line)] bg-white hover:border-[color:var(--pws-coral)]/50"
                  }`}
                  data-testid="contact-phone"
                >
                  <Phone className="h-8 w-8 mx-auto mb-2 text-[color:var(--pws-coral)]" />
                  <div className="font-semibold text-[color:var(--pws-charcoal)]">Text</div>
                </button>
              </div>
              {formData.contactMethod && (
                <div className="bg-white p-4 rounded-2xl border border-[color:var(--pws-line)]" style={{animation: 'slide-in 0.3s ease-out forwards'}}>
                  <Input
                    key={`contact-input-${formData.contactMethod}`}
                    type={formData.contactMethod === "email" ? "email" : "tel"}
                    placeholder={formData.contactMethod === "email" ? "your@email.com" : "Your phone number"}
                    value={formData.contactValue}
                    onChange={(e) => setFormData({ ...formData, contactValue: e.target.value })}
                    className="h-12 rounded-xl border-[color:var(--pws-line)] text-center"
                    data-testid="contact-value"
                    autoFocus
                  />
                  <Button
                    onClick={goNext}
                    disabled={!formData.contactValue}
                    className="w-full mt-3 btn-warm h-12 rounded-xl"
                    data-testid="contact-next"
                  >
                    Continue
                    <ArrowRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              )}
              <button
                onClick={goBack}
                className="mt-6 flex items-center justify-center w-full text-[color:var(--pws-stone)] hover:text-[color:var(--pws-charcoal)] transition-colors"
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Back
              </button>
            </div>
          )}

          {/* Step 8: Review */}
          {step === 8 && (
            <div className={slideClass} key="step8">
              <div className="text-center mb-6">
                <Package className="h-12 w-12 mx-auto text-[color:var(--pws-coral)] mb-4 package-animation" />
                <h1 className="font-['Space_Grotesk'] text-3xl md:text-4xl font-bold text-[color:var(--pws-charcoal)]">
                  Looking good! 👀
                </h1>
                <p className="mt-2 text-[color:var(--pws-stone)]">
                  Double-check and we'll get these out to you
                </p>
              </div>
              
              <div className="bg-white rounded-2xl border border-[color:var(--pws-line)] overflow-hidden">
                <div className="p-5 border-b border-[color:var(--pws-line)]">
                  <div className="flex justify-between items-center">
                    <span className="text-[color:var(--pws-stone)]">Style</span>
                    <span className="font-semibold text-[color:var(--pws-charcoal)]">
                      {styleOptions.find(s => s.value === formData.style)?.label || formData.style}
                    </span>
                  </div>
                </div>
                <div className="p-5 border-b border-[color:var(--pws-line)]">
                  <div className="flex justify-between items-center">
                    <span className="text-[color:var(--pws-stone)]">Size</span>
                    <span className="font-semibold text-[color:var(--pws-charcoal)]">{formData.size}</span>
                  </div>
                </div>
                <div className="p-5 border-b border-[color:var(--pws-line)]">
                  <div className="flex justify-between items-center">
                    <span className="text-[color:var(--pws-stone)]">Color</span>
                    <span className="font-semibold text-[color:var(--pws-charcoal)]">
                      {colorOptions.find(c => c.value === formData.color)?.label || formData.color}
                    </span>
                  </div>
                </div>
                <div className="p-5 border-b border-[color:var(--pws-line)]">
                  <div className="flex justify-between items-start">
                    <span className="text-[color:var(--pws-stone)]">Ship to</span>
                    <span className="font-semibold text-[color:var(--pws-charcoal)] text-right">
                      {formData.name}<br />
                      {formData.street}{formData.apt && `, ${formData.apt}`}<br />
                      {formData.city}, {formData.state} {formData.zip}
                    </span>
                  </div>
                </div>
                {formData.wantMore && formData.contactValue && (
                  <div className="p-5">
                    <div className="flex justify-between items-center">
                      <span className="text-[color:var(--pws-stone)]">Updates via</span>
                      <span className="font-semibold text-[color:var(--pws-charcoal)]">
                        {formData.contactMethod === "email" ? "Email" : "Text"}: {formData.contactValue}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              <div className="mt-4 p-4 bg-[color:var(--pws-cream)] rounded-xl text-center">
                <p className="text-sm text-[color:var(--pws-stone)]">
                  By clicking below, you confirm you're 18+ and work in the adult industry.
                  <br />That's all we need to know. 💜
                </p>
              </div>

              <div className="mt-6 flex gap-3">
                <Button
                  variant="outline"
                  onClick={goBack}
                  className="flex-1 h-14 rounded-xl border-[color:var(--pws-line)]"
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Back
                </Button>
                <Button
                  onClick={handleSubmit}
                  disabled={loading}
                  className="flex-[2] btn-warm btn-warm-pulse h-14 rounded-xl text-lg font-semibold"
                  data-testid="submit-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      Send my panties! 
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Trust footer */}
      <div className="py-4 text-center">
        <p className="text-sm text-[color:var(--pws-stone)]">
          No data sold • No spam • Ships discreetly
        </p>
      </div>
    </div>
  );
}
